import { createTheme, lighten } from '@mui/material/styles';
import * as Sentry from '@sentry/browser';
import {
    ACCESS_BUDGETS_GESTION,
    ACCESS_CADRAGE_GESTION,
    ACCESS_DAF,
    ADMIN,
    ADMIN_CATEGORIES,
    SUPER_FINANCES_WRITER,
    SUPPORT,
} from 'business/constants';
import type { ProtectionT, ScopeT, UserT } from 'business/definitions';
import type { VisionState } from 'components/App/types';
import { getHomePageUrl } from 'helpers/helpers';
import { getPublicToken } from 'helpers/urlHelpers';
import _ from 'lodash';
import type { AuthResponse } from 'routes/LoginPage/types';
import { getClientBind } from 'store/APIClient';
import { PRIMARY_COLOR, SECONDARY_COLOR, THEME_BASE } from 'styles/theme';

const DEFAULT_THEME = createTheme(THEME_BASE);

const emptyUser: UserT = {
    authMethods: [],
    email: '',
    gestionnaireNodes: [],
    homeDashboardSlug: '',
    id: 0,
    isActive: false,
    connectionStatus: 'inactive',
    isAdmin: false,
    isScopeGestionnaireUnrestricted: false,
    isScopeOrgChartUnrestricted: false,
    orgChartNodes: [],
    rights: [],
    roles: [],
    scopes: [],
    writeScopes: [],
    tutorialSteps: {},
    firstname: '',
    lastname: '',
    gender: undefined,
    job: '',
};

const visionState: VisionState = {
    isAuthenticated: false,
    clientLoadedAfterAuth: false,
    hasAccessDaf: false,
    isSuperFinancesWriter: false,
    isAdminCategories: false,
    hasAccessCadrageGestion: false,
    hasAccessBudgetsGestion: false,
    isAdmin: false,
    isSupportUser: false,
    initialLoad: true,
    user: emptyUser,
    client: {
        authMethods: [],
        bind: getClientBind(),
        isValidBind: true,
        hasDatagouv: false,
        logoUrl: '',
        name: '',
        primaryColor: PRIMARY_COLOR,
        secondaryColor: SECONDARY_COLOR,
        theme: DEFAULT_THEME,
        modules: [],
        extraFeatures: [],
        decisionPerimeters: [],
        owner: '',
        homePageUrl: null,
        hasUsageHistory: false,
    },
    protections: [],
    scopes: [],
    writeScopes: [],
    isPdfModeActivated: window.location.pathname.endsWith('/pdf'),
    homePageUrl: null,
};

const visionModel = {
    state: visionState,
    reducers: {
        setClientDetails(state: VisionState, payload: ClientT): VisionState {
            let { theme } = state.client;
            if (payload.primaryColor && payload.secondaryColor) {
                theme = createTheme(
                    _.merge(THEME_BASE, {
                        palette: {
                            primary: {
                                main: payload.primaryColor,
                                background: lighten(payload.primaryColor, 0.85),
                            },
                            secondary: {
                                main: payload.secondaryColor,
                                background: lighten(
                                    payload.secondaryColor,
                                    0.85,
                                ),
                            },
                        },
                    }),
                );
            }
            document.title = `Manty ${payload.name}`;

            return {
                ...state,
                initialLoad: false,
                client: { ...payload, theme },
                homePageUrl: getHomePageUrl(payload),
                clientLoadedAfterAuth: state.isAuthenticated,
            };
        },
        setAuthenticatedState(
            state: VisionState,
            payload: AuthResponse,
        ): VisionState {
            const { token, user } = payload;
            localStorage.setItem('token', token);
            return {
                ...state,

                initialLoad: false,
                isAuthenticated: true,
                isAdmin: user.roles.includes(ADMIN),
                hasAccessDaf: user.roles.includes(ACCESS_DAF),
                isSuperFinancesWriter: user.roles.includes(
                    SUPER_FINANCES_WRITER,
                ),
                isAdminCategories: user.roles.includes(ADMIN_CATEGORIES),
                hasAccessCadrageGestion: user.roles.includes(
                    ACCESS_CADRAGE_GESTION,
                ),
                hasAccessBudgetsGestion: user.roles.includes(
                    ACCESS_BUDGETS_GESTION,
                ),
                isSupportUser: user.roles.includes(SUPPORT),
                user,
            };
        },
        setLogoutState(state: VisionState): VisionState {
            localStorage.removeItem('token');
            return {
                ...state,
                isAuthenticated: false,
                isAdmin: false,
                hasAccessDaf: false,
                isSuperFinancesWriter: false,
                hasAccessCadrageGestion: false,
                isAdminCategories: false,
                hasAccessBudgetsGestion: false,
                user: emptyUser,
            };
        },
        setScopes(
            state: VisionState,
            payload: { scopes: ScopeT[] },
        ): VisionState {
            return {
                ...state,
                scopes: payload.scopes,
            };
        },
        setProtections(
            state: VisionState,
            payload: { protections: ProtectionT[] },
        ): VisionState {
            return {
                ...state,
                protections: payload.protections,
            };
        },
        setUserTutorialSteps(
            state: VisionState,
            payload: { tutorialSteps: { [key: string]: boolean } },
        ): VisionState {
            return {
                ...state,
                user: {
                    ...state.user,
                    tutorialSteps: payload.tutorialSteps,
                },
            };
        },
        updateUserInformation(state: VisionState, newUser: UserT): VisionState {
            return {
                ...state,
                user: newUser,
            };
        },
    },
    // dispatch is 'any' until we upgrade rematch to correctly type it
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    effects: (dispatch: any) => ({
        async logout() {
            return dispatch.API.post({
                params: {
                    endpoint: 'auth/logout',
                },
                successAction: () => {
                    Sentry.setUser(null);
                    dispatch.vision.setLogoutState();
                    // TODO: Use a more elegant way to reset the state.
                    dispatch({
                        type: 'RESET_STATE',
                    });
                },
            });
        },

        async fetchClientDetails() {
            const bind = getClientBind();
            const publicToken = getPublicToken();
            const endpointPrefix = publicToken
                ? `?public_token=${publicToken}`
                : '';
            return dispatch.API.get({
                params: {
                    endpoint: `meta/client/${bind}${endpointPrefix}`,
                },
                successAction: (response: Omit<ClientT, 'isValidBind'>) => {
                    dispatch.vision.setClientDetails({
                        ...response,
                        isValidBind: true,
                    });
                },
                failureAction: (status: number) => {
                    if (status === 404) {
                        dispatch.vision.setClientDetails({
                            ...visionState.client,
                            isValidBind: false,
                        });
                    }
                },
            });
        },

        async checkAuthStatus(payload: { shouldBlacklist: boolean }) {
            return dispatch.API.get({
                params: {
                    endpoint: payload.shouldBlacklist
                        ? 'auth/status?blacklist=true'
                        : 'auth/status',
                },
                // @ts-expect-error [TS migration] Was not detected by flow
                successAction: (response) => {
                    this.setAuthenticatedState(response);
                },
                failureAction: () => {
                    this.setLogoutState();
                },
            });
        },

        async fetchScopes() {
            dispatch.API.get({
                params: { endpoint: 'auth/scopes' },
                // @ts-expect-error [TS migration] Was not detected by flow
                successAction: (response) => {
                    this.setScopes({ scopes: response.scopes });
                },
            });
        },

        async fetchProtections() {
            dispatch.API.get({
                params: { endpoint: 'auth/protections' },
                // @ts-expect-error [TS migration] Was not detected by flow
                successAction: (response) => {
                    this.setProtections({ protections: response.protections });
                },
            });
        },

        async updateTutorialSteps(payload: {
            tutorialSteps: { [key: string]: boolean };
        }) {
            const { tutorialSteps } = payload;
            const dataToSend = {
                tutorialSteps,
            };
            dispatch.API.put({
                params: {
                    endpoint: `auth/user_tutorial`,
                    body: dataToSend,
                },
                successAction: () => {
                    this.setUserTutorialSteps({
                        tutorialSteps,
                    });
                },
            });
        },
    }),
};

export default visionModel;
